import moment, { type Moment } from 'moment';
import { IoMdArrowBack } from 'react-icons/io';
import React, { useEffect, useMemo } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import history from 'utils/history';

import useTitle from 'hooks/useTitle';
import { useFetchReminderByIdQuery } from 'hooks/query/useReminderQueries';

import * as routes from 'constants/routes';

import ReminderForm from './ReminderForm';
import Loading from 'components/common/loading/Loading';

type ReminderRouteParams = {
  id?: string;
};

type ReminderRecord = {
  id: number;
  title: string;
  redirectUrl: string | null;
  startDate: Moment;
  endDate: Moment;
};

type ReminderByIdResponse = {
  data: ReminderRecord;
};

type Props = RouteComponentProps<ReminderRouteParams>;

const CreateEditReminder: React.FC<Props> = (props) => {
  const reminderId = props.match.params?.id;
  const title = reminderId ? 'Edit Reminder' : 'Add Reminder';
  const setTitle = useTitle();

  useEffect(() => {
    setTitle(title);
  }, [setTitle, title]);

  const {
    data: response,
    isLoading: loading,
    isError,
  } = useFetchReminderByIdQuery<ReminderByIdResponse>(reminderId, Boolean(reminderId));

  useEffect(() => {
    if (isError) {
      history.push(routes.REMINDERS);
    }
  }, [isError]);

  const handleBackClick = () => (history.length > 1 ? history.goBack() : history.push(routes.REMINDERS));

  const reminder = useMemo<ReminderRecord | null>(() => {
    if (!response?.data) {
      return null;
    }

    const { data } = response;

    return {
      ...data,
      startDate: moment(data.startDate),
      endDate: moment(data.endDate),
    };
  }, [response]);

  return (
    <div className="full-scope-card">
      <div className="full-scope-card__header table-header name-wrap">
        <div className="d-flex flex-row">
          <button
            type="button"
            className="btn btn-shrink btn--alternate btn--curved mr-10 d-inline-flex flex-fix"
            onClick={handleBackClick}
          >
            <IoMdArrowBack size="16px" className="mr-5" />
            Back
          </button>
          <div className="d-flex flex-row">
            <div className="table-header">
              <h3>{title}</h3>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-min-height">
          <Loading />
        </div>
      ) : (
        <ReminderForm reminder={reminder} />
      )}
    </div>
  );
};

export default CreateEditReminder;
